import type { PersonGeneral } from '@quromedical/models';
import {
  Alert,
  Skeleton,
  SkeletonProvider,
  Snackbar,
  Visible,
} from 'design-system';
import { logger } from 'helpers';
import { useFiniteState } from 'hooks/useFiniteState';
import type React from 'react';
import { useCallback } from 'react';
import { strings } from 'strings';

import { GeneralCard } from './card';
import { GeneralForm } from './form';

interface PersonGeneralFormProps {
  canEdit: boolean;
  data?: Partial<PersonGeneral>;
  minimalData?: boolean;
  revalidate?: () => void;
  onSubmit?: (Person: PersonGeneral) => Promise<void>;
}

type FormState = 'initial' | 'editing' | 'submitting' | 'error';

export const PersonGeneralForm: React.FC<PersonGeneralFormProps> = ({
  canEdit,
  data,
  minimalData = false,
  revalidate,
  onSubmit,
}) => {
  const state = useFiniteState<FormState>('initial');

  const handleSubmit = useCallback(
    async (result: PersonGeneral) => {
      state.set('submitting');
      try {
        await onSubmit?.(result);
        state.set('initial');
        revalidate?.();
        return {};
      } catch (error) {
        state.set('error');
        logger.error(error);
        return { error };
      }
    },
    [revalidate, onSubmit, state],
  );

  return (
    <>
      <SkeletonProvider loading={state.is('submitting')}>
        <Skeleton>
          <Visible if={state.isNot('editing')}>
            <GeneralCard
              data={data}
              canEdit={canEdit}
              onEditPress={state.next('editing')}
              minimalData={minimalData}
            />
          </Visible>

          <Visible if={state.is('editing')}>
            <GeneralForm
              data={data}
              handleCancel={state.next('initial')}
              handleSubmit={handleSubmit}
              minimalData={minimalData}
            />
          </Visible>
        </Skeleton>
      </SkeletonProvider>

      <Snackbar onClose={state.next('editing')} isOpen={state.is('error')}>
        <Alert
          backgroundColor="status-critical"
          textColor="white"
          onAction={state.next('editing')}
          actionIcon="close"
          title={strings.ErrorCardTitle}
          body={strings.GenericErrorMessage}
        />
      </Snackbar>
    </>
  );
};
